import { ClerkProvider, Show, SignIn, useClerk } from "@clerk/react";
import {
  LDProvider,
  useFlags,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import logoUrl from "@/assets/logo.svg";

// True only for sessions authenticated through the gate, where Clerk context
// exists; consumers must not call Clerk hooks unless this is true.
const GateSignedInContext = createContext(false);

export function useGateSignedIn() {
  return useContext(GateSignedInContext);
}

export function GateSignOutButton() {
  const { signOut } = useClerk();
  return (
    <button
      type="button"
      onClick={() => signOut()}
      className="text-sm font-medium text-stone-500 hover:text-stone-900"
    >
      Log out
    </button>
  );
}

// Production fallbacks so a missing CI variable cannot silently disable the
// gate. Clerk publishable keys and LaunchDarkly client-side ids are public by
// design (the mobile apps commit theirs); env vars override for dev/staging.
const clerkPublishableKey =
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? "pk_live_Y2xlcmsuc29tby5haSQ";
const launchDarklyClientSideId =
  import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID ??
  "6863078aa2eb110946e2cbad";

type GateProps = {
  flag?: "hotelSavingsCalculatorPasswordGate";
  name: string;
  children: ReactNode;
};

/**
 * Requires a Somo Clerk sign-in, always when no `flag` is given, otherwise
 * while that LaunchDarkly flag is on.
 */
export function PreviewGate({ flag, name, children }: GateProps) {
  if (!flag || import.meta.env.VITE_FORCE_CALCULATOR_GATE === "true") {
    return (
      <ClerkGate clerkPublishableKey={clerkPublishableKey} name={name}>
        {children}
      </ClerkGate>
    );
  }
  if (!launchDarklyClientSideId) {
    return children;
  }
  return (
    <LDProvider clientSideID={launchDarklyClientSideId}>
      <FlagGate
        clerkPublishableKey={clerkPublishableKey}
        flag={flag}
        name={name}
      >
        {children}
      </FlagGate>
    </LDProvider>
  );
}

function FlagGate({
  clerkPublishableKey,
  flag,
  name,
  children,
}: Required<GateProps> & { clerkPublishableKey: string }) {
  const ldClient = useLDClient();
  const gateOn = useFlags()[flag];
  const [ldReady, setLdReady] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }
    let cancelled = false;
    const markReady = () => {
      if (!cancelled) {
        setLdReady(true);
      }
    };
    // Fail open if LaunchDarkly is unreachable: the gate hides a preview,
    // it does not protect secrets, and a stuck loading screen is worse.
    const fallback = setTimeout(markReady, 3000);
    ldClient.waitUntilReady().then(markReady);
    return () => {
      cancelled = true;
      clearTimeout(fallback);
    };
  }, [ldClient]);

  if (!ldReady) {
    return <div className="min-h-screen bg-stone-50" />;
  }
  if (!gateOn) {
    return children;
  }

  return (
    <ClerkGate clerkPublishableKey={clerkPublishableKey} name={name}>
      {children}
    </ClerkGate>
  );
}

function ClerkGate({
  clerkPublishableKey,
  name,
  children,
}: {
  clerkPublishableKey: string;
  name: string;
  children: ReactNode;
}) {
  return (
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <Show when="signed-in">
        <GateSignedInContext.Provider value={true}>
          {children}
        </GateSignedInContext.Provider>
      </Show>
      <Show when="signed-out">
        <GateSignIn name={name} />
      </Show>
    </ClerkProvider>
  );
}

function GateSignIn({ name }: { name: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-stone-50 px-6 py-12">
      <img src={logoUrl} alt="Somo" className="h-10 w-10" />
      <p className="max-w-sm text-center text-sm text-stone-600">
        {name} is in preview. Sign in with your Somo account to continue.
      </p>
      <SignIn routing="hash" forceRedirectUrl={window.location.href} />
    </div>
  );
}
